<template v-if="visible">
	<div v-if="ro" class="avatar-zone readonly-zone"  :style="style" draggable="false">
		<img :src="avatarUrl" alt="" class='avatarImg' />
	</div>
	<span v-else>
		<anchor-element :tag="div" :chain="chain"  @drop.prevent="onDrop" :data-active="active" @mouseenter="mouseActive=true" @mouseleave="mouseActive=false"
			@dragenter.prevent="setActive" @dragover.prevent="setActive" v-bind="tabindexBinding()"
			@dragleave.prevent="setInactive" :class="avatarClass" class="avatar-zone"
			:style="style" @keydown.ctrl.v="onClipboard" @keydown.meta.v="onClipboard">
			<close-cross v-if="showCloseIcon" class="fileZoneCancelIcon" @click.prevent.stop="removeFile"></close-cross>
			<sym-icon v-if="uploading" name="refresh" class="uploadingIcon upload-icon" />
			<img v-else-if="avatarExists" :src="avatarUrl" alt="" class='avatarImg pointer-area' @click.prevent.stop="onFileInput" draggable="false"/>
			<sym-icon v-else name="photo-add" class="upload-icon" @click="onFileInput"/>
			<input type="file" accept="image/*" capture="environment" ref="fileInputEl" @change="onInputChange" style="display: none;" />
		</anchor-element>
		<boat-element tag="div" class="bubbleError tail-top-left" :chain="chain">
			<span>{{ errorMessage }}</span>
		</boat-element>
	</span>
</template>
<script setup lang="ts">
	import { useCrmFormFile } from './CrmFormFile';
	import {ref, computed} from 'vue';
	import CloseCross from "./CloseCross.vue";
	import {injectEnv} from "@fd/Environment";
	import type {Avatar, AvatarFile} from "@t/fd/editor/Avatar";
	import useEditor, {type EditorProps, editorProps} from "@fd/editors/Editor";
	import SymIcon from "@fd/helpers/SymIcon.vue";
	import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
	import BoatElement from "@/ui/paneltree/BoatElement.vue";
	import {ChainRule, createChain} from "@/ui/paneltree/PanelTree.ts";

	
	const MAX_SIZE=1048576; //1MB
	const uprops = defineProps({ ...editorProps });
	const props = uprops as EditorProps;
	const avatarProps = computed<Avatar>(() => props.part as Avatar);
	const {visible, value, clazz, style, ro, errorClass, tabindexBinding} = useEditor(props);
	const { asPromise } = useCrmFormFile();
	
	const autocompletePosition = ref<ChainPlacement>({ah: 0, av: 100, bh: 0, bv: 0, x: 0, y: 0});
	const chain = createChain({rule: autocompletePosition.value}, "first");
	
	const avatarValue = computed<AvatarFile>(() => {
			if (!value.value) {
				value.value = {fileName:null,contentType:null,data:null,url:null};
			}
			return value.value as AvatarFile;
		});
		
	const env = injectEnv();

	const fileInputEl = ref(null);
	const errorMessage = ref("");
	const active = ref(false);
	const mouseActive = ref(false);
	const inActiveTimeout = ref(null);
	const uploading = ref(false);
	
	const avatarExists = computed(() => {
		return avatarValue.value && (avatarValue.value.data || avatarValue.value.url);
	});
	
	const avatarClass = computed(() => {
		return [
			active.value ? "active-area": "",
			(isError.value || errorClass.value) ? "invalidAvatar" : "",
			clazz.value
		];
	});

	const avatarUrl = computed(() => {
		if (avatarValue.value.url) return avatarValue.value.url;
		else if (avatarValue.value.data) {
			return "data:"+avatarValue.value.contentType+";base64, "+avatarValue.value.data;
		}
		return "";
	});
	
	const isError = computed(() => {
		return  errorMessage.value !== null && errorMessage.value !== undefined && errorMessage.value.length>0;
	});
	
	const showCloseIcon = computed(() => {
		return  !uploading.value && avatarExists.value && (env.touch || mouseActive.value);
	});

	function setActive(): void {
		active.value = true;
		clearTimeout(inActiveTimeout.value) // clear the timeout
	};
	
	function setInactive(): void {
		inActiveTimeout.value = setTimeout(() => {
			active.value = false;
		}, 200);
	}
	
	function removeFile(): void {
		avatarValue.value.fileName="";
		avatarValue.value.contentType="";
		avatarValue.value.url="";
		avatarValue.value.data=null;
		avatarExists.value=false;
	}
	
	function onInputChange(e): void {
		console.log("file input");
		setFile(e.target.files);
		e.target.value = null;
	}
	
	function onDrop(e): void {
		console.log("file drag&drop");
		setInactive();

		let dragFiles = e.dataTransfer.files; //drag&drop
		console.log("drag files:"+dragFiles.length);
		if (dragFiles !== null && dragFiles !== undefined && dragFiles.length>0) {
			setFile([...dragFiles]);
		}
		else {//error console log
			if (dragFiles !== null && dragFiles !== undefined) console.log("Undefined e.dataTransfer.files");
			else if (dragFiles.length===0) console.log("e.dataTransfer.files lenght is 0 - empty?");
			showError(dragError);
		}
	};
	
	function onFileInput(): void {
		fileInputEl.value.click();
	}
	
	function onClipboard(): void {
		console.log("on clipboard");
		navigator.permissions.query({name: "clipboard-read"}).then((permission)=>{
			if (permission.state === "granted" || permission.state === "prompt") {
				navigator.clipboard.read()
					.then(clipboardContents=> {
						var promises = [];
						for (const item of clipboardContents) {
							promises.push(asPromise(item));
						}
						Promise.all(promises).then((files) => {
							setFile(files);
						});
					})
					.catch((error) => {
						console.log("error reading clipboard:"+JSON.stringify(error));
						showError(JSON.stringify(error));
					});
			}
			else {
				console.log("Browser clipboard disabled");
				showError("Not allowed to read clipboard.");
			}
		})
		.catch((error) => {
			console.log("error check permissions:"+JSON.stringify(error));
			showError("Problem with browser support:"+error.message);
		});
	}
	
	function validate(uploadedFile: File): boolean {
		errorMessage.value="";
		if (!uploadedFile.type.includes("image")) {
			showError(env.msg('err.invalidAvatarMime'));
			return false;
		}
		return true;
	}
	
	function setFile(newFiles: File[]): void {
		var uploadedFile: File = newFiles[0];
		console.log("File to upload:"+uploadedFile.name+ " with size:"+uploadedFile.size);
		// resetAvatar();
		
		if (validate(uploadedFile)) {
			uploading.value=true;
			avatarValue.value.fileName=uploadedFile.name;
			avatarValue.value.contentType=uploadedFile.type;

			let reader = new FileReader();
			reader.readAsArrayBuffer(uploadedFile);
			reader.onload = function() {
				avatarValue.value.data=window.btoa(bin2string(new Uint8Array(reader.result)));
				avatarExists.value=true;
				avatarValue.value.url="";
				uploading.value=false;
			};
		}
	}
	
	function bin2string(array): string {
		var result = "";
		for(var i = 0; i < array.length; ++i){
			result+= (String.fromCharCode(array[i]));
		}
		return result;
	}
	
	function showError(message: string): void {
		errorMessage.value=message;
		if (isError.value) {
			chain.visible.value = true;
			setTimeout(() => {
			  chain.visible.value = false;
			}, "2000");
		}
	}
</script>

<style>
.avatarImg {
	margin: auto;
	max-height: 150px;
	max-width: 250px;
}

.avatar-zone {
	border: none;
	background-color: var(--bg);
	color: var(--fg);
	box-shadow: var(--input-shadow);
	border-radius: 3px;
	padding: var(--input-h-padding);
	display: flex;
    flex-direction: column;
    justify-content: space-evenly;
	align-items: center;
	transition: .2s ease;
	position: relative;
	box-sizing: border-box;
	min-height: 80px;
	min-width: 150px;
}

.avatar-zone:focus,
.avatar-zone:focus-visible {
	box-shadow: 0 0 2px 2px var(--outline-color);
	outline: none;
}

.readonly-zone {
	background-color: var(--input-disabled-bg) !important;
}

.pointer-area {
	cursor: pointer;
}

.avatar-zone label {
	cursor: pointer;
}

.active-area {
	background-color: var(--placeholder-fg) !important;
}

.upload-icon {
	font-size: 28px;
	opacity: 0.5;
	cursor: pointer;

	&:hover {
		opacity: 1;
		color: var(--link-fg-hover);
	}
}

.invalidAvatar {
	color: var(--error-fg);
    background-color: var(--error-bg);
}

.bubbleError {
	max-width: 20em;
	text-wrap: balance;
	background-color: var(--bg);
	color: var(--fg);
	border: 1px solid var(--border-color);
	padding: 10px;
	border-radius: var(--menu-radius);
	position: relative;
	box-sizing: border-box;
	box-shadow: var(--panel-shadow);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
